import { useEffect, useState } from 'react';

import { getApiUrl } from '../lib/config';
import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import '../components/landing.css';

const SUPPORT_EMAIL = 'support@paddiq.app';

export function SupportPage() {
  useEffect(() => {
    document.title = 'Support for your team | Paddiq';
  }, []);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      subject: String(data.get('subject') ?? ''),
      message: String(data.get('message') ?? ''),
    };

    setSubmitting(true);
    setError(false);

    void fetch(`${getApiUrl()}/api/v1/support/contact`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (res.ok) {
          setSubmitted(true);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="support-hero">
          <div className="landing-container support-hero__inner">
            <p className="section-kicker">Support</p>
            <h1>Keep your team moving.</h1>
            <p className="support-hero__lead">
              Get help with your workspace, session data, or Paddiq Link. Tell us what you’re
              working on and where you need support.
            </p>
          </div>
        </section>

        <section className="support-form-section">
          <div className="landing-container support-form-section__inner">
            <aside className="support-context">
              <h2>Support for your workflow.</h2>
              <p>
                Include the product you’re using, what you expected, and what happened. For logger
                questions, include the model and connection method.
              </p>
              <a href="/resources">Browse setup and analysis guides →</a>
              <a href="/paddiq-link">Explore Paddiq Link →</a>
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </aside>
            {submitted ? (
              <div className="support-success" role="status">
                <div className="support-success__icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2>Message sent!</h2>
                <p>Thanks for reaching out. We'll be in touch shortly.</p>
                <a className="landing-btn landing-btn--primary" href="/">
                  Back to home
                </a>
              </div>
            ) : (
              <form className="support-form" onSubmit={handleSubmit}>
                <div className="support-form__row support-form__row--two">
                  <div className="support-form__field">
                    <label htmlFor="support-name">Name</label>
                    <input
                      id="support-name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="support-form__field">
                    <label htmlFor="support-email">Email</label>
                    <input
                      id="support-email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="support-form__field">
                  <label htmlFor="support-subject">Subject</label>
                  <input
                    id="support-subject"
                    name="subject"
                    type="text"
                    placeholder="What do you need help with?"
                    required
                  />
                </div>

                <div className="support-form__field">
                  <label htmlFor="support-message">Message</label>
                  <textarea
                    id="support-message"
                    name="message"
                    rows={6}
                    placeholder="Describe your issue or question in detail…"
                    required
                  />
                </div>

                <div className="support-form__actions">
                  {error && (
                    <p className="support-form__error" role="alert">
                      Something went wrong. Please try again or email us directly at{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                    </p>
                  )}
                  <button
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending…' : 'Send message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
